"use client";

import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Copy,
  Share2,
  Check,
  Gift,
  Users,
  Award,
  Sparkles,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REFERRAL_COPY = {
  heroBadge: "Invite & Earn",
  heroTitle: "Bring a Classmate to UniUI",
  heroSubtitle:
    "Share your referral link with 200 & 300 level FUTO classmates. When they sign up, you both get 5 bonus questions.",
  yourLink: "Your Referral Link",
  yourCode: "Your Referral Code",
  copy: "Copy link",
  copied: "Copied!",
  share: "Share",
  signedOut: "Sign in to get your personal referral link.",
  note: "Referral rewards are automatically credited immediately upon sign up. One link, unlimited friends.",
};

export default function ReferralPage() {
  const { user, session, loading: authLoading } = useAuth();
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [stats, setStats] = useState<{
    total_referred: number;
    rewards_earned: number;
  }>({
    total_referred: 0,
    rewards_earned: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const [codeRes, statsRes, profileRes] = await Promise.allSettled([
        api.getMyReferralCode(),
        api.getReferralStats(),
        api.getUserProfile(),
      ]);
      // Resolve a real referral code — generate one server-side if none exists yet.
      let code =
        codeRes.status === "fulfilled" ? codeRes.value?.code || null : null;
      if (!code) {
        try {
          const gen = await api.generateReferralCode();
          code = gen?.code || null;
        } catch {
          /* ignore — fall back below */
        }
      }
      setReferralCode(code || user?.id?.slice(0, 8)?.toUpperCase() || "");
      if (statsRes.status === "fulfilled" && statsRes.value) {
        setStats({
          total_referred: statsRes.value.total_referred || 0,
          rewards_earned: statsRes.value.rewards_earned || 0,
        });
      }
      if (profileRes.status === "fulfilled" && profileRes.value) {
        const p = profileRes.value as any;
        setUsername(p?.username || user?.username || "");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load referral data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [session, user]);

  const publicProfileUrl =
    typeof window !== "undefined" && username
      ? `${window.location.origin}/u/${encodeURIComponent(username)}`
      : "";
  // The referral link must always point to the signup page with the ?ref=
  // parameter so the backend can credit both parties. Previously this fell
  // back to the public profile URL (/u/username) which does **not** track
  // referrals — that was the bug.
  const referralLink = `${typeof window !== "undefined" ? window.location.origin : ""}/auth/signup?ref=${encodeURIComponent(referralCode || user?.id || "")}`;

  const handleCopy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Select the link and copy manually.");
    }
  };

  const handleShare = async () => {
    if (!referralLink) return;
    const shareData = {
      title: "UniUI — Your AI study partner",
      text: "Join me on UniUI for 200 & 300 level FUTO coursework and past questions:",
      url: referralLink,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        handleCopy();
      }
    } catch {
      // user cancelled share — ignore
    }
  };

  // While_auth_is_still_resolving, don't show the "Sign in" CTA to an
  // already-authenticated user (user is_briefly_null_on_first_paint, which
  // previously rendered the sign-in block to signed-in visitors).
  if (authLoading) {
    return (
      <PublicLayout>
        <PageTransition>
          <div className="max-w-3xl mx-auto px-4 py-16 flex items-center justify-center min-h-[50vh]">
            <Loader2 className="w-8 h-8 text-y animate-spin" />
          </div>
        </PageTransition>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <PageTransition>
        <div className="max-w-3xl mx-auto px-4 py-16 space-y-8">
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Gift className="w-5 h-5 text-y" />
              <span className="text-xs font-black uppercase tracking-[0.3em] text-y">
                {REFERRAL_COPY.heroBadge}
              </span>
            </div>
            <h1 className="display text-4xl md:text-5xl uppercase tracking-tighter text-foreground">
              {REFERRAL_COPY.heroTitle}
            </h1>
            <p className="text-sm text-muted-foreground font-medium max-w-xl mx-auto">
              {REFERRAL_COPY.heroSubtitle}
            </p>
          </div>

          {!user ? (
            <div className="matte-metal rounded-3xl border border-border p-8 text-center">
              <p className="text-muted-foreground font-medium">
                {REFERRAL_COPY.signedOut}
              </p>
              <Button
                asChild
                className="mt-4 font-black uppercase tracking-widest"
              >
                <a href="/auth/login?redirect=/referral">Sign in</a>
              </Button>
            </div>
          ) : loading ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Skeleton className="h-20 w-full rounded-2xl" />
                <Skeleton className="h-20 w-full rounded-2xl" />
              </div>
              <Skeleton className="h-48 w-full rounded-3xl" />
            </div>
          ) : error ? (
            <div className="matte-metal rounded-3xl border border-border p-8 text-center space-y-3">
              <p className="text-muted-foreground font-medium">{error}</p>
              <Button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  loadData();
                }}
                variant="outline"
                className="mt-2 font-black uppercase tracking-widest"
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="matte-metal rounded-2xl border border-border p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-y/10 flex items-center justify-center text-y">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Classmates Invited
                    </p>
                    <p className="text-2xl font-black text-foreground">
                      {stats.total_referred}
                    </p>
                  </div>
                </div>

                <div className="matte-metal rounded-2xl border border-border p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Bonus Questions Earned
                    </p>
                    <p className="text-2xl font-black text-foreground">
                      +{stats.rewards_earned}
                    </p>
                  </div>
                </div>
              </div>

              {/* Share Box */}
              <div className="matte-metal rounded-3xl border border-y/20 p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                    {REFERRAL_COPY.yourLink}
                  </label>
                  {referralCode && (
                    <span className="text-xs font-bold text-y bg-y/10 px-3 py-1 rounded-full">
                      Code: {referralCode}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <code className="break-all rounded-xl bg-secondary border border-border px-4 py-3 text-sm text-foreground">
                    {referralLink}
                  </code>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={handleCopy}
                      variant="outline"
                      className="font-black uppercase tracking-widest"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied ? REFERRAL_COPY.copied : REFERRAL_COPY.copy}
                    </Button>
                    <Button
                      onClick={handleShare}
                      className="font-black uppercase tracking-widest"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      {REFERRAL_COPY.share}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {REFERRAL_COPY.note}
                </p>
              </div>
            </div>
          )}
        </div>
      </PageTransition>
    </PublicLayout>
  );
}
